/**
 * Custom useSyncStatusIndicator Hook
 * Provides easy access to sync status state and methods in React components
 * Follows the Four-Layer Rule: Components → Hooks → Stores → Services
 */

import { useCallback } from 'react';
import { useAppStore } from '../stores';

export function useSyncStatusIndicator() {
  const isOnline = useAppStore(state => state.chores.syncStatus.isOnline);
  const isSyncing = useAppStore(state => state.chores.syncStatus.isSyncing);
  const pendingActions = useAppStore(state => state.chores.syncStatus.pendingActions);
  const lastSyncTime = useAppStore(state => state.chores.syncStatus.lastSyncTime);
  const lastSyncError = useAppStore(state => state.chores.syncStatus.lastSyncError);
  const failedActions = useAppStore(state => state.chores.syncStatus.failedActions);
  const syncRemoteFromFirestore = useAppStore(state => state.chores.syncRemoteFromFirestore);
  const forceFullResync = useAppStore(state => state.chores.forceFullResync);
  const retryFailedActions = useAppStore(state => state.chores.retryFailedActions);
  const forgetFailedActions = useAppStore(state => state.chores.forgetFailedActions);

  const startSync = useCallback(async () => {
    if (!isOnline || isSyncing) return;
    await syncRemoteFromFirestore();
  }, [isOnline, isSyncing, syncRemoteFromFirestore]);

  const startForceFullResync = useCallback(async () => {
    if (!isOnline || isSyncing) return;
    await forceFullResync();
  }, [isOnline, isSyncing, forceFullResync]);

  return {
    isOnline,
    isSyncing,
    pendingActions,
    lastSyncTime,
    lastSyncError,
    failedActions,
    startSync,
    startForceFullResync,
    retryFailedActions,
    forgetFailedActions
  };
}
